/**
 * UnconnectedModulesPrompt
 * ========================
 * Front-and-centre nudge: the client has ENABLED modules but hasn't connected
 * the data source that powers them — so they're not getting the value. Shows
 * only the modules whose source is missing; renders nothing when everything's
 * connected, in demo mode, or when no enabled module has a known source need.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, ArrowRight } from 'lucide-react';

// module keyword -> { label, sources: provider keywords that satisfy it }
const MODULE_SOURCE_MAP = [
  { match: ['financial', 'finance', 'accounting', 'cashflow', 'cash flow'], label: 'Financial Intelligence', sources: ['xero', 'myob', 'quickbooks'], hint: 'accounting' },
  { match: ['marketing', 'seo', 'social', 'ads', 'advertising'], label: 'Marketing Intelligence', sources: ['google_analytics', 'google', 'meta', 'facebook', 'instagram', 'ga4'], hint: 'marketing/analytics' },
  { match: ['crm', 'customer', 'customerinsights'], label: 'Customer / CRM', sources: ['square', 'pos', 'crm', 'hubspot'], hint: 'POS or CRM' },
  { match: ['roster', 'staff', 'people', 'hr', 'workforce', 'team'], label: 'People & Rostering', sources: ['deputy', 'tanda', 'xero', 'payroll'], hint: 'rostering/payroll' },
  { match: ['inventory', 'stock', 'pos', 'sales', 'retail'], label: 'Sales & Inventory', sources: ['square', 'pos', 'lightspeed', 'shopify'], hint: 'POS' },
  { match: ['reviews', 'reputation', 'feedback'], label: 'Reviews & Reputation', sources: ['google', 'reviews', 'google_business'], hint: 'Google Business' },
];

export default function UnconnectedModulesPrompt({ enabledModules = [], connectedSources = [], isDemo = false, onConnect }) {
  if (isDemo) return null;

  const connected = (connectedSources || []).map((s) => (s || '').toString().toLowerCase());
  const mods = (enabledModules || []).map((m) => (m || '').toString().toLowerCase());

  const unmet = [];
  const seen = new Set();
  MODULE_SOURCE_MAP.forEach((entry) => {
    const isEnabled = mods.some((m) => entry.match.some((kw) => m.includes(kw)));
    if (!isEnabled) return;
    const isConnected = entry.sources.some((src) => connected.some((c) => c.includes(src)));
    if (!isConnected && !seen.has(entry.label)) {
      seen.add(entry.label);
      unmet.push(entry);
    }
  });

  if (unmet.length === 0) return null;

  return (
    <Card className="border-indigo-200 bg-indigo-50/60" data-testid="unconnected-modules-prompt">
      <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="font-semibold text-indigo-900">
              Get the most out of your {unmet.length === 1 ? 'module' : 'modules'}
            </p>
            <p className="text-sm text-indigo-800/80 mt-0.5">
              You've enabled <strong>{unmet.map((u) => u.label).join(', ')}</strong> but haven't connected the data {unmet.length === 1 ? 'source' : 'sources'} that {unmet.length === 1 ? 'powers it' : 'power them'} yet.
            </p>
          </div>
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
          onClick={onConnect}
          data-testid="connect-modules-cta"
        >
          Connect data sources
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}
